import React from 'react';
import PropTypes from 'prop-types';

import {extractIndividualErrors} from 'validation';
import {putAll} from 'utils/Array'

import BaseComponent from '../BaseComponent.js';
import Tooltip from './../Tooltip';
import ErrorTooltip from 'common/ErrorTooltip';
import theme from 'theme/theme.scss';

//Comma num pad key for IE.
const IE_COMMA_KEY = "|^Delete$";
const COMMA = ",";

/**
 * The <Input> component specifies an input field where the user can enter data, and a label that is related with the field.
 */
export default class Input extends BaseComponent {

    checkCopyPermission = (e) => {
        if(this.props.copyDenied) {
            e.preventDefault();
        }
    };

    checkAllowedInput = (event) => {
        let allowedSymbols = this.props.allowedSymbols;

        //Adding allowed symbols for IE.
        if(allowedSymbols && COMMA.match(new RegExp(allowedSymbols))) {
            allowedSymbols = allowedSymbols + IE_COMMA_KEY;
        }

        if(!allowedSymbols || event.key.match(allowedSymbols)) {
            return true;
        } else {
            event.preventDefault();
            return false;
        }
    };

    focus = () => {
        this.input.focus();
    };

    render() {
        const errorMessages = extractIndividualErrors(this.props.errorMessages, this.props.name, this.props.index);

        let titles = [];
        titles = putAll(titles, this.props.title);
        if (!this.props.showExclamationMark) {
            titles = putAll(titles, errorMessages);
        }

        let label;
        if (this.props.label) {
            let labelValue;
            if (this.props.fullLabel) {
                labelValue = <abbr title={this.props.fullLabel}>{this.props.label}</abbr>;
            } else {
                labelValue = this.props.label;
            }
            label = (
                <label htmlFor={this.props.id}>{labelValue}{this.props.required ? <span>*</span> : ""}
                {this.props.tooltip ? <Tooltip for={this.props.id} value={this.props.tooltip}/> : ""}
                </label>
            );
        }

        return (
            <div className={this.props.className}>
                {label}
                <input id={this.props.id} name={this.props.name} value={this.props.value} onChange={this.props.onChange}
                    type={this.props.type} disabled={this.props.disabled} readOnly={this.props.readOnly}
                    checked={this.props.checked} onKeyPress={this.checkAllowedInput}
                    className={errorMessages.length > 0 ? theme.errorField : ""} maxLength={this.props.maxLength}
                    title={titles.length > 0 ? titles.join("\n") : null} onCopy={this.checkCopyPermission} ref={(input) => this.input = input}/>
                {this.props.showExclamationMark && errorMessages.length > 0 ? <ErrorTooltip for={this.props.id} value={errorMessages}/> : ""}
            </div>
        );
    }
}

Input.propTypes = {

    /**
     * Unique element identifier.
     */
    id: PropTypes.string.isRequired,

    /**
     * Value of an input element.
     */
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,

    /**
     * Specifies the name of an element.
     */
    name: PropTypes.string,

    /**
     * Value of an input label.
     */
    label: PropTypes.string,

    /**
     * An additional label if the shown one is abbreviation.
     */
    fullLabel: PropTypes.string,

    /**
     * JavaScript function that has to be invoked on element value change.
     */
    onChange: PropTypes.func,

    /**
     * Specifies that an input field must be filled out before submitting.
     */
    required: PropTypes.bool,

    /**
     * Specifies that an element should be disabled.
     */
    disabled: PropTypes.bool,

    /**
     * Specifies that an element should be readOnly.
     */
    readOnly: PropTypes.bool,

    /**
     * Tooltip that has to be shown on the right side of input element.
     */
    tooltip: PropTypes.string,

    /**
     * Specifies the type <Input> element to display.
     */
    type: PropTypes.string,

    /**
     * When present, it specifies that an element should be pre-selected (checked).
     */
    checked: PropTypes.bool,

    /**
     * Specifies CSS class to display.
     */
    className: PropTypes.string,

    /**
     * JavaScript function that has to be invoked on key press.
     */
    onKeyPress: PropTypes.func,

    /**
     * Number of maximum characters in input.
     */
    maxLength: PropTypes.number,

    /**
     * Title for input.
     */
    title: PropTypes.string,

    /**
     * Is it necessary to show exclamation mark icon if error occurs (true by default)?
     */
    showExclamationMark: PropTypes.bool,

    /**
     * Flag to show copy permission.
     */
    copyDenied: PropTypes.bool,

    /**
     * Regular expression with symbols, which can be added to input.
     */
    allowedSymbols: PropTypes.string
};

Input.defaultProps = {
    type: "text",
    showExclamationMark: true
};


// WEBPACK FOOTER //
// ./src/common/Input/index.js